import React, { useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import API_BASE_URL from "../api";
import logo1 from "../resource/logo1.png";
import logo2 from "../resource/logo2.png";
import logo3 from "../resource/logo3.png";
import logo4 from "../resource/logo4.png";
import { AuthContext } from "../AuthContext";
import downloadIcon from "../resource/Vector.png";
import { QRCodeSVG } from "qrcode.react";
import { generateCertificateHtml } from "../utils/certificateTemplate";
import {
  Leaf,
  Truck,
  MapPin,
  Navigation,
  Ruler,
  Weight,
  Sparkles,
  Download,
  X,
  ArrowRight,
  ShieldCheck,
  FileCheck2,
  Lock
} from "lucide-react";

export const handleDownload = async (pdfUrl) => {
  try {
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.target = "_blank";
    a.download = "certificate.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (error) {
    console.error("Error downloading PDF:", error);
  }
};

export const CarbonFootprint = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const userId = user?.userId;

  const [formData, setFormData] = useState({
    VechileNumber: "",
    SourcePincode: "",
    DestinationPincode: "",
    MobilizationDistance: "",
    DemobilizationDistance: "",
    LoadedWeight: "",
  });
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [showSignupMessage, setShowSignupMessage] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [calculating, setCalculating] = useState(false);

  const userName = user?.userName || user?.baseUsername || "";

  useEffect(() => {
    const isFirstTimeData = localStorage.getItem("isFirstTime");
    setIsFirstTime(isFirstTimeData === "false" ? false : true);
    setShowSignupMessage(!userId && isFirstTimeData === "false" ? true : false);
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value.toUpperCase(),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResponse(null);
    setShowSignupMessage(false);
    setCalculating(true);

    try {
      if (!userId) {
        if (isFirstTime) {
          const { data } = await axios.post(
            `${API_BASE_URL}/api/vehicle/getCarbonFootPrints`,
            formData
          );

          setResponse(data);
          setShowSignupMessage(true);
          localStorage.setItem("isFirstTime", "false");

          const isFirstTimeData = localStorage.getItem("isFirstTime");
          setIsFirstTime(isFirstTimeData === "false" ? false : true);
        } else {
          setShowSignupMessage(true);
        }
      } else {
        const { data } = await axios.post(
          `${API_BASE_URL}/api/vehicle/findCO2Emission`,
          {
            ...formData,
            userId,
          }
        );
        setResponse(data);

        const certificateHtml = generateCertificateHtml({
          certificateNumber: data.certificateNumber,
          certificateIssueDate: data.certificateIssueDate,
          vehicleNumber: data.vehicleNumber,
          co2Emission: data.co2Emission,
          userName: userName || "",
          baseUsername: userName || "",
          dpiitLogo: logo1,
          startupLogo: logo2,
          pureLogo: `${window.location.origin}${logo3}`,
          signImage: logo4,
        });

        const pdfUrlData = await axios.post(
          `${API_BASE_URL}/api/vehicle/generateCarbonFootprintPDF`,
          {
            html: certificateHtml,
            id: data.id,
            userId,
          }
        );
        setPdfUrl(pdfUrlData.data.signedUrl);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "An error occurred while calculating emissions."
      );
    } finally {
      setCalculating(false);
    }
  };

  const closeModal = () => {
    setResponse(null);
    setFormData({
      VechileNumber: "",
      SourcePincode: "",
      DestinationPincode: "",
      MobilizationDistance: "",
      DemobilizationDistance: "",
      LoadedWeight: "",
    });
  };

  const formFields = [
    {
      name: "VechileNumber",
      label: "Vehicle Number",
      placeholder: "e.g. BR01GB5618",
      icon: Truck,
      required: true,
    },
    {
      name: "SourcePincode",
      label: "Source Pincode",
      placeholder: "e.g. 110001",
      icon: MapPin,
      required: true,
    },
    {
      name: "DestinationPincode",
      label: "Destination Pincode",
      placeholder: "e.g. 400001",
      icon: Navigation,
      required: true,
    },
    {
      name: "MobilizationDistance",
      label: "Mobilization Distance (km)",
      placeholder: "e.g. 15 (Optional)",
      icon: Ruler,
      required: false,
    },
    {
      name: "DemobilizationDistance",
      label: "Demobilization Distance (km)",
      placeholder: "e.g. 10 (Optional)",
      icon: Ruler,
      required: false,
    },
    {
      name: "LoadedWeight",
      label: "Loaded Cargo Weight (Tons)",
      placeholder: "e.g. 12.5",
      icon: Weight,
      required: true,
    },
  ];

  return (
    <div className="min-h-screen w-full bg-slate-50 font-sans text-slate-900 pb-20">
      {/* Top Hero Dark Emerald Header for Transparent Navbar Contrast */}
      <div className="bg-gradient-to-br from-[#0E3B2E] via-[#092b21] to-[#041611] text-white pt-36 pb-20 px-4 sm:px-6 relative overflow-hidden">
        {/* Subtle Grid Accent Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(#10B981_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 backdrop-blur-md px-4 py-1.5 rounded-full mb-4 shadow-sm"
          >
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-xs sm:text-sm font-bold text-emerald-300 tracking-wide uppercase">
              AI-Powered Emission Intelligence
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-6xl font-extrabold text-white tracking-tight leading-tight"
          >
            Carbon Footprint Calculator
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-sm sm:text-lg text-emerald-100/90 mt-3 font-medium leading-relaxed max-w-2xl mx-auto"
          >
            Calculate precise vehicle transport emissions and generate official sustainability data for your fleet.
          </motion.p>
        </div>
      </div>

      {/* Main Crisp White Calculator Container */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-10 relative z-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.98, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 lg:p-12 shadow-2xl relative overflow-hidden text-slate-900"
        >
          {/* Top Emerald Gradient Line */}
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#0E3B2E] via-emerald-500 to-teal-400" />

          {/* Form Header */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-6 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#0E3B2E] text-emerald-400 flex items-center justify-center shadow-md">
                <Leaf className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">Vehicle Emission Calculator</h3>
                <p className="text-xs text-slate-500">Fill in vehicle & route metrics below</p>
              </div>
            </div>

            <span className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-50 px-3.5 py-1.5 rounded-full border border-emerald-200">
              <ShieldCheck className="w-4 h-4 text-emerald-700" /> ISO Standard Compliant
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {formFields.map((field) => {
                const IconComponent = field.icon;
                return (
                  <div key={field.name} className="space-y-2">
                    <label
                      htmlFor={field.name}
                      className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider flex items-center gap-1.5"
                    >
                      <IconComponent className="w-4 h-4 text-emerald-700 shrink-0" />
                      <span>{field.label}</span>
                      {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <div className="relative flex items-center">
                      <input
                        type="text"
                        id={field.name}
                        name={field.name}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        required={field.required}
                        className="w-full bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm sm:text-base rounded-xl px-4 py-3.5 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 transition font-medium"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Submit Action Button */}
            <div className="pt-6 flex flex-col items-center">
              <button
                type="submit"
                disabled={(!isFirstTime && !userId) || calculating}
                className="w-full sm:w-auto px-10 py-4 bg-[#0E3B2E] hover:bg-emerald-800 text-white font-bold text-base sm:text-lg rounded-xl shadow-lg transition transform hover:scale-[1.01] flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {calculating ? (
                  <span>Calculating Emission...</span>
                ) : (
                  <>
                    <span>Calculate Carbon Footprint</span>
                    <ArrowRight className="w-5 h-5 text-emerald-400" />
                  </>
                )}
              </button>
            </div>
          </form>

          {error && (
            <div className="mt-6 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm font-semibold text-center">
              {error}
            </div>
          )}

          {/* Signup Alert Banner */}
          {showSignupMessage && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 p-5 rounded-2xl bg-emerald-50 border border-emerald-200 text-center flex flex-col sm:flex-row items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3 text-left">
                <div className="w-10 h-10 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center shrink-0">
                  <Lock className="w-5 h-5 text-emerald-800" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Unlock Unlimited Emission Reports</h4>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Sign up or log in to generate downloadable PDF certificates and store fleet logs.
                  </p>
                </div>
              </div>
              <a
                href="/signup"
                className="shrink-0 bg-[#0E3B2E] hover:bg-emerald-800 text-white font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition shadow"
              >
                Sign Up Now
              </a>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Clean White Emission Results Modal */}
      {response && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white border border-slate-200 text-slate-900 rounded-3xl p-6 sm:p-8 shadow-2xl max-w-lg w-full relative overflow-hidden"
          >
            {/* Top Accent Line */}
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#0E3B2E] to-emerald-500" />

            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full transition"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Title Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-[#0E3B2E] text-emerald-400 flex items-center justify-center">
                <FileCheck2 className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                  Emission Details
                </h3>
                <p className="text-xs text-slate-500">Verified carbon footprint results</p>
              </div>
            </div>

            {/* Total CO2 Stat Badge */}
            <div className="mb-6 p-5 rounded-2xl bg-emerald-50 border border-emerald-200 text-center shadow-inner">
              <span className="text-xs font-bold text-emerald-800 uppercase tracking-widest block mb-1">
                Total CO₂ Emission
              </span>
              <div className="text-4xl font-black text-[#0E3B2E] tracking-tight flex items-center justify-center gap-2">
                <span>{response.co2Emission}</span>
                <span className="text-lg font-bold text-emerald-700">kg CO₂</span>
              </div>
            </div>

            {/* Details Table */}
            <div className="space-y-3 bg-slate-50 p-4 rounded-2xl border border-slate-200 text-xs sm:text-sm">
              <div className="flex justify-between py-1.5 border-b border-slate-200">
                <span className="text-slate-600 font-medium">Vehicle Number:</span>
                <span className="font-bold text-slate-900">{response.vehicleNumber}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-200">
                <span className="text-slate-600 font-medium">Certificate Number:</span>
                <span className="font-bold text-slate-900 font-mono">{response.certificateNumber}</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-slate-600 font-medium">Issue Date:</span>
                <span className="font-bold text-slate-900">{response.certificateIssueDate}</span>
              </div>
            </div>

            {/* PDF Download & QR Code Section */}
            {userId && pdfUrl ? (
              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between gap-4">
                <button
                  onClick={() => handleDownload(pdfUrl)}
                  className="flex-1 bg-[#0E3B2E] hover:bg-emerald-800 text-white font-bold py-3.5 rounded-xl shadow transition flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="w-5 h-5 text-emerald-400" />
                  <span>Download Certificate PDF</span>
                </button>

                <div className="flex flex-col items-center bg-slate-50 p-2 rounded-2xl border border-slate-200">
                  <QRCodeSVG value={pdfUrl} size={64} />
                  <span className="text-[10px] text-slate-500 font-semibold mt-1">Scan PDF</span>
                </div>
              </div>
            ) : (
              <div className="mt-6 pt-5 border-t border-slate-100 text-center">
                <p className="text-xs text-slate-600 mb-3">
                  Log in to generate & download official green PDF certificates.
                </p>
                <a
                  href="/login"
                  className="inline-flex items-center gap-2 bg-[#0E3B2E] hover:bg-emerald-800 text-white text-xs font-bold px-5 py-2.5 rounded-xl transition shadow"
                >
                  <span>Log In to Download PDF</span>
                  <ArrowRight className="w-4 h-4 text-emerald-400" />
                </a>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};